import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";

import usePdfFieldsRules from "../../../hooks/usePdfFieldsRules";
import PDFFieldsSection from "./PDFFieldsSection";
import useAuth from "../../../hooks/useAuth";
import { useProject } from "../../../contexts/project";

export default function PdfFieldsSettings() {
  const { user } = useAuth();
  const { settings } = user.company;
  const receivingWizardEnabled = settings.receiving_wizard;
  const inventoryTrackingEnabled = settings.inventory_tracking;
  const project = useProject();
  const onsiteCaptureToWarehouseEnabled = project?.settings?.onsite_capture_to_warehouse_storage === true;
  const { values, setFieldValue } = useFormikContext();
  const shipmentFields = usePdfFieldsRules("shipment", values?.fieldSettings);
  const inventoryRequestFields = usePdfFieldsRules("inventory_request", values?.fieldSettings);
  const itemLabelFields = usePdfFieldsRules("shipment_item", values?.fieldSettings);
  const moveLabelFields = usePdfFieldsRules("shipment_item_move_label", values?.fieldSettings);

  const inboundFields = values?.shipmentPdfFields?.ordered_fields || [];
  const outboundFields = values?.inventoryRequestPdf?.ordered_fields || [];
  const itemLabelSelectedFields = values?.shipmentItemPdfFields?.ordered_fields || [];
  const moveLabelOrderedFields = values?.shipmentItemMoveLabelPdfFields?.ordered_fields || [];

  const inboundFieldObjects = useMemo(() => {
    return inboundFields
      .map(fieldName => shipmentFields.find(field => field[0] === fieldName))
      .filter(Boolean);
  }, [inboundFields, shipmentFields]);

  const outboundFieldObjects = useMemo(() => {
    return outboundFields
      .map(fieldName => inventoryRequestFields.find(field => field[0] === fieldName))
      .filter(Boolean);
  }, [outboundFields, inventoryRequestFields]);

  const moveLabelFieldObjects = useMemo(() => {
    return moveLabelOrderedFields
      .map(fieldName => moveLabelFields.find(field => field[0] === fieldName))
      .filter(Boolean);
  }, [moveLabelOrderedFields, moveLabelFields]);


  // Transform ordered fields array to positioned fields object
  const positionedFields = useMemo(() => ({
    position_1: itemLabelSelectedFields[0] ? itemLabelFields.find(field => field[0] === itemLabelSelectedFields[0]) : null,
    position_2: itemLabelSelectedFields[1] ? itemLabelFields.find(field => field[0] === itemLabelSelectedFields[1]) : null,
    position_3: itemLabelSelectedFields[2] ? itemLabelFields.find(field => field[0] === itemLabelSelectedFields[2]) : null,
  }), [itemLabelSelectedFields, itemLabelFields]);

  // Transform positioned fields back to ordered array with placeholders
  const transformPositionedToOrdered = (positioned) => {
    return [
      positioned.position_1 ? positioned.position_1[0] : "placeholder_position_1",
      positioned.position_2 ? positioned.position_2[0] : "placeholder_position_2", 
      positioned.position_3 ? positioned.position_3[0] : "placeholder_position_3"
    ];
  };

  const handleInboundReorder = reorderedFields => {
    const fieldNames = reorderedFields.map(field => field[0]);
    // Only save the first 10 fields (PDF column limit)
    const viewableFieldNames = fieldNames.slice(0, 10);
    setFieldValue("shipmentPdfFields.ordered_fields", viewableFieldNames);
  };

  const handleOutboundReorder = reorderedFields => {
    const fieldNames = reorderedFields.map(field => field[0]);
    // Only save the first 12 fields (PDF column limit)
    const viewableFieldNames = fieldNames.slice(0, 12);
    setFieldValue("inventoryRequestPdf.ordered_fields", viewableFieldNames);
  };

  const handleInboundRemove = fieldId => {
    const newFields = inboundFields.filter(fieldName => fieldName !== fieldId);
    setFieldValue("shipmentPdfFields.ordered_fields", newFields);
  };

  const handleOutboundRemove = fieldId => {
    const newFields = outboundFields.filter(fieldName => fieldName !== fieldId);
    setFieldValue("inventoryRequestPdf.ordered_fields", newFields);
  };

  const handleInboundAdd = field => {
    const newFields = [...inboundFields, field[0]];
    setFieldValue("shipmentPdfFields.ordered_fields", newFields);
  };

  const handleOutboundAdd = field => {
    const newFields = [...outboundFields, field[0]];
    setFieldValue("inventoryRequestPdf.ordered_fields", newFields);
  };

  const handleMoveLabelReorder = reorderedFields => {
    const fieldNames = reorderedFields.map(field => field[0]);
    const viewableFieldNames = fieldNames.slice(0, 8);
    setFieldValue("shipmentItemMoveLabelPdfFields.ordered_fields", viewableFieldNames);
  };

  const handleMoveLabelRemove = fieldId => {
    const newFields = moveLabelOrderedFields.filter(fieldName => fieldName !== fieldId);
    setFieldValue("shipmentItemMoveLabelPdfFields.ordered_fields", newFields);
  };

  const handleMoveLabelAdd = field => {
    const newFields = [...moveLabelOrderedFields, field[0]];
    setFieldValue("shipmentItemMoveLabelPdfFields.ordered_fields", newFields);
  };


  // New handlers for positioned fields
  const handlePositionedAdd = (position, field) => {
    const newPositioned = { ...positionedFields };
    newPositioned[`position_${position}`] = field;
    const orderedFields = transformPositionedToOrdered(newPositioned);
    setFieldValue("shipmentItemPdfFields.ordered_fields", orderedFields);
  };

  const handlePositionedRemove = (position) => {
    const newPositioned = { ...positionedFields };
    newPositioned[`position_${position}`] = null;
    const orderedFields = transformPositionedToOrdered(newPositioned);
    setFieldValue("shipmentItemPdfFields.ordered_fields", orderedFields);
  };

  return (
    <Box display="flex" flexDirection="column" mt={1} gap={3} width="100%">
      {receivingWizardEnabled && (
        <PDFFieldsSection
          title="Inbound Receiving Report PDF Fields"
          fields={inboundFieldObjects}
          onReorder={handleInboundReorder}
          onRemove={handleInboundRemove}
          onAdd={handleInboundAdd}
          availableFields={shipmentFields}
          maxFields={10}
          pdfType="shipment"
        />
      )}
      {inventoryTrackingEnabled && (
        <PDFFieldsSection
          title="Outbound Order PDF Fields"
          fields={outboundFieldObjects}
          onReorder={handleOutboundReorder}
          onRemove={handleOutboundRemove}
          onAdd={handleOutboundAdd}
          availableFields={inventoryRequestFields}
          maxFields={12}
          pdfType="inventory_request"
        />
      )}
      <PDFFieldsSection
        title="Picture Label PDF Fields"
        fields={moveLabelFieldObjects}
        onReorder={handleMoveLabelReorder}
        onRemove={handleMoveLabelRemove}
        onAdd={handleMoveLabelAdd}
        availableFields={moveLabelFields}
        maxFields={8}
        pdfType="shipment_item_move_label"
        enablePreview={false}
      />
      {receivingWizardEnabled && (
        <PDFFieldsSection
          title="Item Labels PDF Fields"
          availableFields={itemLabelFields}
          enablePreview={false}
          maxFields={3}
          pdfType="shipment_item"
          isItemLabel={true}
          positionedFields={positionedFields}
          onPositionedAdd={handlePositionedAdd}
          onPositionedRemove={handlePositionedRemove}
        />
      )}
    </Box>
  );
}
